import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  CircularProgress,
  Avatar,
  FormGroup,
  Checkbox
} from '@mui/material';
import {
  Computer,
  Add,
  Edit,
  Delete,
  Settings,
  Power,
  PowerOff,
  Warning,
  CheckCircle,
  Build,
  Analytics,
  Print,
  CreditCard,
  TouchApp,
  Scanner,
  Refresh,
  Download,
  History
} from '@mui/icons-material';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`kiosk-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface Kiosk {
  kioskId: string;
  name: string;
  location: string;
  status: 'online' | 'offline' | 'maintenance';
  lastHeartbeat: string;
  isActive: boolean;
  hardware: {
    printerConnected: boolean;
    scannerConnected: boolean;
    cardReaderConnected: boolean;
    touchscreenEnabled: boolean;
  };
}

interface KioskSession {
  _id: string;
  kioskId: string;
  status: string;
  startedAt: string;
  lastActivity: string;
  statistics: {
    totalTransactions: number;
    checkIns: number;
    checkOuts: number;
    payments: number;
  };
}

const KioskManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [kiosks, setKiosks] = useState<Kiosk[]>([]);
  const [sessions, setSessions] = useState<KioskSession[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [configuration, setConfiguration] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState('PRWR');

  // Dialog states
  const [addKioskDialog, setAddKioskDialog] = useState(false);
  const [configDialog, setConfigDialog] = useState(false);
  const [selectedKiosk, setSelectedKiosk] = useState<Kiosk | null>(null);

  // Form states
  const [newKioskData, setNewKioskData] = useState({
    name: '',
    location: '',
    kioskId: ''
  });

  useEffect(() => {
    fetchData();
    // Set up auto-refresh for kiosk status
    const interval = setInterval(fetchKioskHealth, 30000);
    return () => clearInterval(interval);
  }, [selectedLocation]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchKioskHealth(),
        fetchSessions(),
        fetchAnalytics(),
        fetchConfiguration()
      ]);
    } catch (error) {
      console.error('Error fetching kiosk data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchKioskHealth = async () => {
    try {
      const response = await fetch(`/api/kiosk/admin/health/${selectedLocation}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setKiosks(data);
    } catch (error) {
      console.error('Error fetching kiosk health:', error);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await fetch(`/api/kiosk/admin/sessions/${selectedLocation}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setSessions(data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`/api/kiosk/admin/analytics/${selectedLocation}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchConfiguration = async () => {
    try {
      const response = await fetch(`/api/kiosk/admin/configuration/${selectedLocation}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setConfiguration(data);
    } catch (error) {
      console.error('Error fetching configuration:', error);
    }
  };

  const handleAddKiosk = async () => {
    try {
      const response = await fetch(`/api/kiosk/admin/register/${selectedLocation}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newKioskData)
      });

      if (response.ok) {
        setAddKioskDialog(false);
        setNewKioskData({ name: '', location: '', kioskId: '' });
        fetchKioskHealth();
      }
    } catch (error) {
      console.error('Error adding kiosk:', error);
    }
  };

  const handleToggleMaintenance = async (kioskId: string, maintenanceMode: boolean) => {
    try {
      await fetch(`/api/kiosk/admin/kiosk/${selectedLocation}/${kioskId}/maintenance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ maintenanceMode })
      });

      fetchKioskHealth();
    } catch (error) {
      console.error('Error toggling maintenance mode:', error);
    }
  };

  const handleUpdateConfiguration = async (updates: any) => {
    try {
      const response = await fetch(`/api/kiosk/admin/configuration/${selectedLocation}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        fetchConfiguration();
      }
    } catch (error) {
      console.error('Error updating configuration:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
        return <CheckCircle color="success" />;
      case 'offline':
        return <PowerOff color="error" />;
      case 'maintenance':
        return <Build color="warning" />;
      default:
        return <Warning color="disabled" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return '#4caf50';
      case 'offline':
        return '#f44336';
      case 'maintenance':
        return '#ff9800';
      default:
        return '#9e9e9e';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          Kiosk Management
        </Typography>
        <Box>
          <FormControl size="small" sx={{ minWidth: 120, mr: 2 }}>
            <InputLabel>Location</InputLabel>
            <Select
              value={selectedLocation}
              label="Location"
              onChange={(e) => setSelectedLocation(e.target.value)}
            >
              <MenuItem value="PRWR">Pioneer RV</MenuItem>
              <MenuItem value="ARVA">Arrowhead RV</MenuItem>
              <MenuItem value="SRMA">Sunridge RV</MenuItem>
              <MenuItem value="SPRI">Sunset Palm RV</MenuItem>
              <MenuItem value="AVSW">Adobe Village RV</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData}>
            Refresh
          </Button>
        </Box>
      </Box>

      <Paper>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Overview" />
          <Tab label="Kiosk Devices" />
          <Tab label="Active Sessions" />
          <Tab label="Configuration" />
          <Tab label="Analytics" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            {/* Status Summary */}
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                      <Computer />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">{kiosks.length}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Kiosks
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                      <Power />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {kiosks.filter(k => k.status === 'online').length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Online
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                      <TouchApp />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {sessions.filter(s => s.status === 'active').length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Active Sessions
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                      <Analytics />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {analytics?.totalTransactions || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Today's Transactions
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Transaction Breakdown */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Transaction Types (Today)</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={[
                      { type: 'Check-Ins', count: analytics?.totalCheckIns || 0 },
                      { type: 'Check-Outs', count: analytics?.totalCheckOuts || 0 },
                      { type: 'Payments', count: analytics?.totalPayments || 0 },
                      { type: 'Amenity Orders', count: 0 }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="type" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#1976d2" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Kiosk Status Distribution */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Kiosk Status</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Online', value: kiosks.filter(k => k.status === 'online').length, fill: '#4caf50' },
                          { name: 'Offline', value: kiosks.filter(k => k.status === 'offline').length, fill: '#f44336' },
                          { name: 'Maintenance', value: kiosks.filter(k => k.status === 'maintenance').length, fill: '#ff9800' }
                        ]}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label
                      />
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Registered Kiosks</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setAddKioskDialog(true)}
            >
              Add Kiosk
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Kiosk ID</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Hardware</TableCell>
                  <TableCell>Last Heartbeat</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {kiosks.map((kiosk) => (
                  <TableRow key={kiosk.kioskId}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {getStatusIcon(kiosk.status)}
                        <Typography sx={{ ml: 1 }}>{kiosk.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {kiosk.kioskId}
                      </Typography>
                    </TableCell>
                    <TableCell>{kiosk.location}</TableCell>
                    <TableCell>
                      <Chip
                        label={kiosk.status}
                        size="small"
                        sx={{ 
                          bgcolor: getStatusColor(kiosk.status), 
                          color: 'white' 
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        {kiosk.hardware.printerConnected && (
                          <Chip icon={<Print />} label="Printer" size="small" />
                        )}
                        {kiosk.hardware.scannerConnected && (
                          <Chip icon={<Scanner />} label="Scanner" size="small" />
                        )}
                        {kiosk.hardware.cardReaderConnected && (
                          <Chip icon={<CreditCard />} label="Card Reader" size="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {kiosk.lastHeartbeat 
                        ? new Date(kiosk.lastHeartbeat).toLocaleString()
                        : 'Never'
                      }
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleMaintenance(
                          kiosk.kioskId, 
                          kiosk.status !== 'maintenance'
                        )}
                      >
                        <Build />
                      </IconButton>
                      <IconButton size="small" onClick={() => setSelectedKiosk(kiosk)}>
                        <Settings />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Typography variant="h6" gutterBottom>Active Sessions</Typography>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Session ID</TableCell>
                  <TableCell>Kiosk</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Last Activity</TableCell>
                  <TableCell>Transactions</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.filter(s => s.status === 'active').map((session) => (
                  <TableRow key={session._id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {session._id.substring(0, 8)}...
                      </Typography>
                    </TableCell>
                    <TableCell>{session.kioskId}</TableCell>
                    <TableCell>{new Date(session.startedAt).toLocaleString()}</TableCell>
                    <TableCell>{new Date(session.lastActivity).toLocaleString()}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          Check-Ins: {session.statistics.checkIns}
                        </Typography>
                        <Typography variant="body2">
                          Check-Outs: {session.statistics.checkOuts}
                        </Typography>
                        <Typography variant="body2">
                          Payments: {session.statistics.payments}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Button size="small" color="error">
                        End Session
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <Typography variant="h6" gutterBottom>Kiosk Configuration</Typography>
          
          {configuration && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>Features</Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={configuration.features.selfCheckIn}
                          onChange={(e) => handleUpdateConfiguration({
                            features: { ...configuration.features, selfCheckIn: e.target.checked }
                          })}
                        />
                      }
                      label="Self Check-In"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={configuration.features.selfCheckOut}
                          onChange={(e) => handleUpdateConfiguration({
                            features: { ...configuration.features, selfCheckOut: e.target.checked }
                          })}
                        />
                      }
                      label="Self Check-Out"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={configuration.features.paymentProcessing}
                          onChange={(e) => handleUpdateConfiguration({
                            features: { ...configuration.features, paymentProcessing: e.target.checked }
                          })}
                        />
                      }
                      label="Payment Processing"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={configuration.features.amenityOrdering}
                          onChange={(e) => handleUpdateConfiguration({
                            features: { ...configuration.features, amenityOrdering: e.target.checked }
                          })}
                        />
                      }
                      label="Amenity Ordering"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={configuration.features.multiLanguage}
                          onChange={(e) => handleUpdateConfiguration({
                            features: { ...configuration.features, multiLanguage: e.target.checked }
                          })}
                        />
                      }
                      label="Multi-Language Support"
                    />
                  </FormGroup>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>Security Settings</Typography>
                  <TextField
                    fullWidth
                    label="Session Timeout (minutes)"
                    type="number"
                    value={configuration.securitySettings.sessionTimeoutMinutes}
                    onChange={(e) => handleUpdateConfiguration({
                      securitySettings: { 
                        ...configuration.securitySettings, 
                        sessionTimeoutMinutes: parseInt(e.target.value) 
                      }
                    })}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Max Failed Attempts"
                    type="number"
                    value={configuration.securitySettings.maxFailedAttempts}
                    onChange={(e) => handleUpdateConfiguration({
                      securitySettings: { 
                        ...configuration.securitySettings, 
                        maxFailedAttempts: parseInt(e.target.value) 
                      }
                    })}
                    sx={{ mb: 2 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={configuration.securitySettings.logAllTransactions}
                        onChange={(e) => handleUpdateConfiguration({
                          securitySettings: { 
                            ...configuration.securitySettings, 
                            logAllTransactions: e.target.checked 
                          }
                        })}
                      />
                    }
                    label="Log All Transactions"
                  />
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>Check-In Settings</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={configuration.checkInSettings.printParkingPass}
                            onChange={(e) => handleUpdateConfiguration({
                              checkInSettings: { 
                                ...configuration.checkInSettings, 
                                printParkingPass: e.target.checked 
                              }
                            })}
                          />
                        }
                        label="Print Parking Pass"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={configuration.checkInSettings.printSiteMap}
                            onChange={(e) => handleUpdateConfiguration({
                              checkInSettings: { 
                                ...configuration.checkInSettings, 
                                printSiteMap: e.target.checked 
                              }
                            })}
                          />
                        }
                        label="Print Site Map"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={configuration.checkInSettings.sendTextConfirmation}
                            onChange={(e) => handleUpdateConfiguration({
                              checkInSettings: { 
                                ...configuration.checkInSettings, 
                                sendTextConfirmation: e.target.checked 
                              }
                            })}
                          />
                        }
                        label="Send Text Confirmation"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={configuration.checkInSettings.earlyCheckInAllowed}
                            onChange={(e) => handleUpdateConfiguration({
                              checkInSettings: { 
                                ...configuration.checkInSettings, 
                                earlyCheckInAllowed: e.target.checked 
                              }
                            })}
                          />
                        }
                        label="Allow Early Check-In"
                      />
                      {configuration.checkInSettings.earlyCheckInAllowed && (
                        <TextField
                          fullWidth
                          label="Early Check-In Fee"
                          type="number"
                          value={configuration.checkInSettings.earlyCheckInFee}
                          onChange={(e) => handleUpdateConfiguration({
                            checkInSettings: { 
                              ...configuration.checkInSettings, 
                              earlyCheckInFee: parseFloat(e.target.value) 
                            }
                          })}
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <Typography variant="h6" gutterBottom>Analytics & Reports</Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Transaction Volume (Last 7 Days)</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={analytics?.dailyBreakdown || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="totalTransactions" stroke="#8884d8" name="Total" />
                      <Line type="monotone" dataKey="checkIns" stroke="#82ca9d" name="Check-Ins" />
                      <Line type="monotone" dataKey="checkOuts" stroke="#ffc658" name="Check-Outs" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Performance Metrics</Typography>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="Average Transaction Time"
                        secondary={`${Math.round(analytics?.avgTransactionTime || 0)} seconds`}
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemText
                        primary="System Uptime"
                        secondary={`${analytics?.uptime || 100}%`}
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemText
                        primary="Total Revenue Processed"
                        secondary={`$${(analytics?.totalRevenue || 0).toLocaleString()}`}
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemText
                        primary="Error Rate"
                        secondary={`${((analytics?.totalErrors || 0) / (analytics?.totalTransactions || 1) * 100).toFixed(2)}%`}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Peak Usage Hours</Typography>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={Object.entries(analytics?.peakUsageHours || {}).map(([hour, count]) => ({
                      hour: `${hour}:00`,
                      count
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#ff7300" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" gap={2}>
                <Button variant="contained" startIcon={<Download />}>
                  Export Report
                </Button>
                <Button variant="outlined" startIcon={<History />}>
                  View Historical Data
                </Button>
              </Box>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Add Kiosk Dialog */}
      <Dialog open={addKioskDialog} onClose={() => setAddKioskDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Register New Kiosk</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Kiosk Name"
            value={newKioskData.name}
            onChange={(e) => setNewKioskData({ ...newKioskData, name: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Physical Location"
            value={newKioskData.location}
            onChange={(e) => setNewKioskData({ ...newKioskData, location: e.target.value })}
            helperText="e.g., Front Desk, Pool Area, etc."
          />
          <TextField
            fullWidth
            margin="normal"
            label="Kiosk ID (Optional)"
            value={newKioskData.kioskId}
            onChange={(e) => setNewKioskData({ ...newKioskData, kioskId: e.target.value })}
            helperText="Leave blank to auto-generate"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddKioskDialog(false)}>Cancel</Button>
          <Button onClick={handleAddKiosk} variant="contained">Register Kiosk</Button>
        </DialogActions>
      </Dialog>

      {/* Configuration Dialog */}
      <Dialog open={configDialog} onClose={() => setConfigDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Advanced Configuration</DialogTitle>
        <DialogContent>
          {/* Additional configuration options */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialog(false)}>Close</Button>
          <Button variant="contained">Save Changes</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KioskManagementPage;